import type { EngineEvent } from '../../interfaces/websocket-events.interface'
import {
  addToolCallEntity,
  updateToolCallEntity,
  updateToolCallEntityProgress,
} from '../slices/chat.slice'
import {
  asBoolean,
  asNumber,
  asRecord,
  asString,
  asStringOrNull,
  asStringOrUndefined,
  extractIds,
  type IdContext,
} from './engine-event-primitives.helper'
import { isActiveConversation, type WebSocketStoreApi } from './websocket-store.helper'

const resolveToolEventContext = (
  store: WebSocketStoreApi,
  event: EngineEvent
): { ids: IdContext; category: string } | null => {
  const ids = extractIds(event)
  if (!ids) return null
  if (!isActiveConversation(store, ids.conversationId)) return null

  const category = asString(event.data.category) || 'external'
  return { ids, category }
}

export const handleAgentToolCall = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const ctx = resolveToolEventContext(store, event)
  if (!ctx) return
  const { ids, category } = ctx

  const threadId = asStringOrUndefined(event.agent_execution_thread_id)
  store.dispatch(
    addToolCallEntity({
      conversationId: ids.conversationId,
      parentEventId: ids.messageId,
      toolCallId: asString(event.data.tool_call_id) || event.event_id,
      toolName: asString(event.data.tool_slug),
      toolArgs: asRecord(event.data.args),
      agentName: asStringOrUndefined(event.agent_node_slug),
      agentLabel: asStringOrUndefined(event.data.agent_label),
      toolCategory: category,
      threadId,
      parentThreadId: asStringOrNull(event.data.parent_thread_id),
      startedAt: event.ts,
    })
  )
}

export const handleAgentToolResult = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const ctx = resolveToolEventContext(store, event)
  if (!ctx) return
  const { ids } = ctx

  const isError = asBoolean(event.data.is_error)
  const isNoData = asBoolean(event.data.is_no_data)
  const preview = asString(event.data.preview)
  const status: 'error' | 'no-data' | 'completed' = isError
    ? 'error'
    : isNoData
      ? 'no-data'
      : 'completed'
  store.dispatch(
    updateToolCallEntity({
      parentEventId: ids.messageId,
      toolCallId: asString(event.data.tool_call_id) || event.event_id,
      status,
      output: preview || undefined,
      errorMessage: isError ? preview || undefined : undefined,
      htmlResultKey: asStringOrUndefined(event.data.html_result_key),
      finishedAt: event.ts,
    })
  )
}

export const handleToolProgress = (store: WebSocketStoreApi, event: EngineEvent): void => {
  const ids = extractIds(event)
  if (!ids) return
  if (!isActiveConversation(store, ids.conversationId)) return

  const toolCallId = asString(event.data.tool_call_id) || asString(event.data.tool_slug)
  if (!toolCallId) return

  store.dispatch(
    updateToolCallEntityProgress({
      parentEventId: ids.messageId,
      toolCallId,
      message: asString(event.data.message),
      percentage: asNumber(event.data.percentage, 0),
    })
  )
}
